import { useRef, useState, useLayoutEffect } from 'react';
import { motion } from 'motion/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { TourStep } from '../types/tour.types';

interface TourTooltipProps {
  step: TourStep;
  stepIndex: number;
  totalSteps: number;
  targetRect: DOMRect | null;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
  isLast: boolean;
  /** Paso de acción (X3): sin «Saltar» ni «Finalizar»; «Lo hago después» lo cierra. */
  esAccion?: boolean;
  /** Sin la insignia del nivel (respuesta de Sebastián: en los recorridos de una demo). */
  sinNivel?: boolean;
  /** Ronda Y (demo): un botón que lleva a otra pantalla (p. ej. «Ir a Estrategia»). */
  boton?: { texto: string; onIr: () => void };
}

const TOOLTIP_OFFSET = 16;
const VIEWPORT_MARGIN = 8;

export function TourTooltip({
  step,
  stepIndex,
  totalSteps,
  targetRect,
  onNext,
  onPrev,
  onClose,
  isLast,
  esAccion = false,
  sinNivel = false,
  boton,
}: TourTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [measuredHeight, setMeasuredHeight] = useState(200);

  useLayoutEffect(() => {
    if (tooltipRef.current) {
      setMeasuredHeight(tooltipRef.current.getBoundingClientRect().height);
    }
  }, [step.id, stepIndex]);

  const position = posicionDelTooltip(targetRect, step.placement, measuredHeight);

  return (
    <motion.div
      ref={tooltipRef}
      key={step.id}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.2 }}
      className="fixed z-[9999] w-80"
      style={{ top: position.top, left: position.left }}
    >
      <div className="bg-popover border border-border rounded-lg shadow-xl p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              {stepIndex + 1} de {totalSteps}
            </span>
            {!sinNivel && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                {step.levels[0]}
              </Badge>
            )}
          </div>
          <button
            type="button"
            aria-label="Cerrar recorrido"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <h4 className="font-semibold text-sm mb-1">{step.title}</h4>
        <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
        {boton && (
          <Button
            size="sm"
            variant="outline"
            onClick={boton.onIr}
            className="mt-3 h-7 px-3 text-xs"
          >
            {boton.texto}
          </Button>
        )}

        {/* Progress bar */}
        <div className="mt-3 mb-3 h-1 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-300"
            style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
          />
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          {esAccion ? (
            <span />
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-xs text-muted-foreground"
            >
              Saltar tour
            </Button>
          )}
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={onPrev}
              disabled={stepIndex === 0}
              className="h-7 w-7 p-0"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {esAccion ? (
              <Button size="sm" variant="outline" onClick={onClose} className="h-7 px-3 text-xs">
                Lo hago después
              </Button>
            ) : (
              <Button size="sm" onClick={onNext} className="h-7 px-3 text-xs">
                {isLast ? 'Finalizar' : 'Siguiente'}
                {!isLast && <ChevronRight className="w-3 h-3 ml-1" />}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Arrow */}
      <Arrow placement={position.lado} targetRect={targetRect} />
    </motion.div>
  );
}

type Lado = TourStep['placement'];

const OPUESTO: Record<Lado, Lado> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

/**
 * Dónde va el globo del recorrido. Si el lado pedido no entra en la ventana, va al opuesto
 * y, si tampoco, arriba o abajo (vault 349: en el teléfono, un `right` contra una barra a
 * lo ancho quedaba encima de lo que señalaba). Si no entra por ningún lado, se queda con el
 * pedido. Siempre dentro de la ventana. Devuelve el lado elegido para la flecha.
 */
export function posicionDelTooltip(
  rect: DOMRect | null,
  placement: Lado,
  tooltipHeight: number,
  ventana: { ancho: number; alto: number } = {
    ancho: window.innerWidth,
    alto: window.innerHeight,
  }
): { top: number; left: number; lado: Lado } {
  if (!rect) return { top: 100, left: 100, lado: placement };

  const tooltipWidth = 320;
  const { ancho: viewportWidth, alto: viewportHeight } = ventana;

  const entra: Record<Lado, boolean> = {
    top: rect.top - tooltipHeight - TOOLTIP_OFFSET >= VIEWPORT_MARGIN,
    bottom: rect.bottom + TOOLTIP_OFFSET + tooltipHeight <= viewportHeight - VIEWPORT_MARGIN,
    left: rect.left - TOOLTIP_OFFSET - tooltipWidth >= VIEWPORT_MARGIN,
    right: rect.right + TOOLTIP_OFFSET + tooltipWidth <= viewportWidth - VIEWPORT_MARGIN,
  };
  const candidatos: Lado[] = [
    placement,
    OPUESTO[placement],
    ...(['top', 'bottom'] as Lado[]).filter((l) => l !== placement && l !== OPUESTO[placement]),
  ];
  const lado = candidatos.find((l) => entra[l]) ?? placement;

  let top: number;
  let left: number;

  switch (lado) {
    case 'bottom':
      top = rect.bottom + TOOLTIP_OFFSET;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      break;
    case 'top':
      top = rect.top - tooltipHeight - TOOLTIP_OFFSET;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.left - tooltipWidth - TOOLTIP_OFFSET;
      break;
    case 'right':
      top = rect.top + rect.height / 2 - tooltipHeight / 2;
      left = rect.right + TOOLTIP_OFFSET;
      break;
  }

  // Clamp to viewport bounds so the tooltip (and its buttons) never overflow
  top = Math.max(VIEWPORT_MARGIN, Math.min(top, viewportHeight - tooltipHeight - VIEWPORT_MARGIN));
  left = Math.max(VIEWPORT_MARGIN, Math.min(left, viewportWidth - tooltipWidth - VIEWPORT_MARGIN));

  return { top, left, lado };
}

function Arrow({
  placement,
  targetRect,
}: {
  placement: TourStep['placement'];
  targetRect: DOMRect | null;
}) {
  if (!targetRect) return null;

  const baseClasses = 'absolute w-3 h-3 bg-popover border rotate-45';

  // Arrow should stay within tooltip width (320px - some padding)
  const maxArrowLeft = 320 - 20;

  switch (placement) {
    case 'bottom':
      return (
        <div
          className={`${baseClasses} border-t border-l -top-1.5`}
          style={{
            left:
              Math.min(Math.max(20, targetRect.left + targetRect.width / 2 - 6), maxArrowLeft) +
              'px',
          }}
        />
      );
    case 'top':
      return (
        <div
          className={`${baseClasses} border-b border-r -bottom-1.5`}
          style={{
            left:
              Math.min(Math.max(20, targetRect.left + targetRect.width / 2 - 6), maxArrowLeft) +
              'px',
          }}
        />
      );
    case 'left':
      return (
        <div className={`${baseClasses} border-r border-t -right-1.5`} style={{ top: '40px' }} />
      );
    case 'right':
      return (
        <div className={`${baseClasses} border-l border-b -left-1.5`} style={{ top: '40px' }} />
      );
  }
}
